import type { IProgressPromise, IProgressPromiseGenerator } from '@monofe/util';
import type { IMediaAPI } from '../../api/interface/media';
import type { IAuthenticationManager } from '../../controller/interface/auth';
import type { IMediumDto } from '../../model';
import { log } from '../../private';

interface IMediaResponseType {
    Blob: Blob;
    ArrayBuffer: ArrayBuffer;
    none: undefined;
}

const DEFAULT_MEDIUM_SIZE = 5242880; /* 5MiB */

const _clamp = (value: number, min: number, max: number): number =>
    Math.max(Math.min(value, max), min);

function _isMediumDto(object: unknown): object is IMediumDto {
    const isNullOrUndefined = (value: unknown): boolean => value === null || value === undefined;

    if (isNullOrUndefined(object) || typeof object !== 'object') {
        return false;
    }

    const record: Partial<IMediumDto> = object as Partial<IMediumDto>;

    return (
        !isNullOrUndefined(record.id) &&
        !isNullOrUndefined(record.mediumHash) &&
        !isNullOrUndefined(record.fileName) &&
        !isNullOrUndefined(record.mimeType) &&
        !isNullOrUndefined(record.mediumSize)
    );
}

export function download<K extends keyof IMediaResponseType, V extends IMediaResponseType[K]>(
    promiser: IProgressPromiseGenerator,
    mediaAPI: () => IMediaAPI[],
    type: K,
    media: (IMediumDto | string)[],
    auth: IAuthenticationManager,
): IProgressPromise<V[]> {
    return promiser.promise(async (resolve, reject, progressCb) => {
        const accessToken = await auth.renew();
        let fetchError: string | null = null;

        progressCb(0.01);

        const totalBytesToDownload = media.reduce(
            (total: number, medium: IMediumDto | string) =>
                total + (_isMediumDto(medium) ? medium.mediumSize : DEFAULT_MEDIUM_SIZE),
            0,
        );

        const responses: V[] = [];

        let progressUptoThisFetch = 0; /* As we loop through keep a running counter of all weightings up to this point */

        /* Download each fetch in order sequentially */
        for (const medium of media) {
            const mediaId = _isMediumDto(medium) ? medium.id : medium;
            const mediumSize = _isMediumDto(medium) ? medium.mediumSize : DEFAULT_MEDIUM_SIZE;

            /* How much is this request worth overall to the progress */
            const progressWeighting = mediumSize / totalBytesToDownload;

            /* Fix warning: https://eslint.org/docs/rules/no-loop-func */
            const progressCounterSoFar = progressUptoThisFetch;

            const parameters = { accessToken };

            const fetchers: (() => IProgressPromise<V> | null)[] = mediaAPI().map(
                api => () => api.getMedia(type, mediaId, parameters),
            );

            const fetchErrors: unknown[] = [];

            let result: V | null = null;

            for (const fetchLoader of fetchers) {
                const fetcher = fetchLoader();

                fetcher?.onProgress(progressNow => {
                    const newProgress = progressCounterSoFar + progressNow * progressWeighting;
                    progressCb(_clamp(newProgress, 0, 1));
                });

                try {
                    result = await fetcher;

                    if (type === 'Blob' && result) {
                        responses.push(result);
                    }

                    progressUptoThisFetch += progressWeighting;

                    progressCb(_clamp(progressUptoThisFetch, 0, 1));

                    break; /* We're done, skip over rest of fetchers & move to next medium download */
                } catch (error) {
                    fetchErrors.push(error);
                    continue;
                }
            }

            if (fetchErrors.length > 0 && !result) {
                fetchError = `Download all media failed: ${JSON.stringify(fetchErrors)}`;
                log.error(fetchError);
            }
        }

        if (fetchError && fetchError.length > 0) {
            throw new Error(fetchError ?? 'Download all media failed');
        }

        return responses;
    });
}

export default download;
